import { api, FilterOptionsResponse } from "../index";
import {
	AffiliateCreateDTO,
	AffiliateCreateCouponDTO,
	AffiliateSaleDTO,
	AffiliateSalesByMonthDTO,
	AffiliateStatsResponse,
	AffiliateTransferDTO
} from "./dto/affiliate.dto";

const USERS_ENDPOINT = "/users";

export const AffiliateAPI = api.injectEndpoints({
	endpoints: build => ({
		getUserAffiliate: build.query<{ affiliate: AffiliateCreateDTO }, null>({
			query: () => ({ url: `${USERS_ENDPOINT}/getAffiliate`, method: "GET" }),
			providesTags: ["userAffiliate"]
		}),
		getUserAffiliateCoupon: build.query<{ coupon: AffiliateCreateCouponDTO }, { affiliate_id: string }>({
			query: ({ affiliate_id }) => ({ url: `${USERS_ENDPOINT}/affiliateCoupon/${affiliate_id}`, method: "GET" }),
			providesTags: ["userAffiliateCoupon"]
		}),
		createAffiliate: build.mutation<{ affiliate: AffiliateCreateDTO }, AffiliateCreateDTO>({
			query: ({
				business_name,
				payout_method,
				holder_name,
				payout_wallet_number,
				payout_bank_account_number,
				payout_bank_name
			}) => ({
				url: `${USERS_ENDPOINT}/createAfiliate`,
				method: "POST",
				body: {
					business_name,
					payout_method,
					holder_name,
					payout_wallet_number,
					payout_bank_account_number,
					payout_bank_name
				}
			}),
			invalidatesTags: ["userAffiliate"]
		}),
		createAffiliateCoupon: build.mutation<{ coupon: AffiliateCreateCouponDTO }, AffiliateCreateCouponDTO>({
			query: body => ({
				url: `${USERS_ENDPOINT}/createCoupon`,
				method: "POST",
				body
			}),
			invalidatesTags: ["userAffiliateCoupon"]
		}),
		validateCoupon: build.query<
			{ valid: boolean; coupon: AffiliateCreateCouponDTO },
			{ code: string; invitationId?: string }
		>({
			query: ({ code, invitationId }) => ({
				url: `${USERS_ENDPOINT}/validateCoupon`,
				method: "GET",
				params: { code, invitationId }
			})
		}),
		getAffiliateSales: build.query<
			FilterOptionsResponse<AffiliateSaleDTO[]> & {
				totalSales: number;
				totalCommission: number;
			},
			{ page: number; perPage: number; from: string; to: string }
		>({
			query: ({ from, to, page, perPage }) => ({
				url: `${USERS_ENDPOINT}/affiliateSales`,
				method: "GET",
				params: { from, to, page, perPage }
			}),
			providesTags: ["userAffiliateSales"]
		}),
		getAffiliateTransfers: build.query<
			FilterOptionsResponse<AffiliateTransferDTO[]>,
			{ page: number; perPage: number }
		>({
			query: ({ page, perPage }) => ({
				url: `${USERS_ENDPOINT}/transfers`,
				method: "GET",
				params: { page, perPage }
			}),
			providesTags: ["userAffiliateTransfers"]
		}),
		getUserAffiliateStats: build.query<AffiliateStatsResponse, {}>({
			query: () => ({ url: `${USERS_ENDPOINT}/affiliateStats`, method: "GET" }),
			providesTags: ["userAffiliateStats"]
		}),
		getAffiliateSalesByMonth: build.query<{ sales: AffiliateSalesByMonthDTO[] }, {}>({
			query: () => ({ url: `${USERS_ENDPOINT}/affiliateSalesByMonth`, method: "GET" }),
			providesTags: ["userAffiliateSalesByMonth"]
		})
	})
});

export const {
	useGetUserAffiliateQuery,
	useCreateAffiliateMutation,
	useLazyGetUserAffiliateCouponQuery,
	useCreateAffiliateCouponMutation,
	useLazyValidateCouponQuery,
	useLazyGetAffiliateSalesQuery,
	useLazyGetAffiliateTransfersQuery,
	useGetUserAffiliateStatsQuery,
	useGetAffiliateSalesByMonthQuery
} = AffiliateAPI;
